import { ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { FiltersService } from 'src/app/core/services/filters.service';
import { FilterRequests } from 'src/app/core/services/filter-requests.service';
import { HierarchyCriteria, HierarchyNode } from 'src/app/global/ebr-models';
import { FilterSet } from 'src/app/global/filter-model';


@Component({
    selector: 'view-selection',
    templateUrl: './view-selection.component.html',
    standalone: false
})
export class ViewSelectionComponent implements OnInit, OnDestroy {

  @ViewChild('viewSelectionDialog') dialogRef: ElementRef<HTMLElement> | undefined;

  isOpen: boolean = false;
  isLoading: boolean = false;
  hasError: boolean = false;

  criteria: HierarchyCriteria | undefined;
  nodes: HierarchyNode[] = [];
  PARENT_LEVEL: number = 1; // H5 level is the parent level in the hierarchy
  CHILD_LEVEL: number = 2; // H7 level is the child level in the hierarchy
  LEAF_LEVEL: number = 3; // Account level is the leaf level in the hierarchy
  template: string | undefined;
  private filterSet: FilterSet | undefined;
  private readonly expanded: Set<string> = new Set<string>();
  private readonly subscriptions: Subscription = new Subscription();

  constructor(
    private readonly filtersService: FiltersService,
    private readonly filterRequests: FilterRequests,
    private readonly cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.subscriptions.add(
      this.filtersService.filterSetObservable.subscribe(filterSet => this.filterSet = filterSet)
    );
    this.template = this.filterSet?.Template ?? '';
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /** Figma renders the card subtitle as Region: y |  Country: z", omitting empty parts. */
  criteriaSummary(node: HierarchyNode): string {
  return [
          node.region && `Region: ${node.region}`,
          node.country && `Country: ${node.country}`
]
  .filter(Boolean)
  .join(' | ');
  }

  open(): void {
    this.isOpen = true;
    // Defer so the dialog exists in the DOM before focus moves into it.
    setTimeout(() => this.dialogRef?.nativeElement.focus());
    void this.loadHierarchy();
  }

  close(): void {
    this.isOpen = false;
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.isOpen) {
      this.close();
    }
  }

  hasChildren(node: HierarchyNode): boolean {
    return !!node.children?.length;
  }

  /** H5/H7 rows, i.e. everything that keeps drilling down. */
  getHierarchyChildren(node: HierarchyNode): HierarchyNode[] {
    return (node.children ?? []).filter(child => child.level !== this.LEAF_LEVEL);
  }

  getAccountChildren(node: HierarchyNode): HierarchyNode[] {
    return (node.children ?? []).filter(child => child.level === this.LEAF_LEVEL);
  }

  hasHierarchyChildren(node: HierarchyNode): boolean {
    return this.getHierarchyChildren(node).length > 0;
  }

  hasAccountChildren(node: HierarchyNode): boolean {
    return this.getAccountChildren(node).length > 0;
  }

  canExpandNode(node: HierarchyNode): boolean {
    return node.level !== this.LEAF_LEVEL && this.hasChildren(node);
  }

  isExpanded(node: HierarchyNode): boolean {
    return this.expanded.has(node.id ?? '');
  }

  toggle(node: HierarchyNode): void {
    if (!this.canExpandNode(node)) {
      return;
    }

    const id = node.id ?? '';
    if (this.expanded.has(id)) {
      this.expanded.delete(id);
    } else {
      this.expanded.add(id);
    }
  }

  summary(node: HierarchyNode): string {

    // If the node is a leaf (Account level), return an empty string as there are no further children to summarize.
    if (node.level === this.LEAF_LEVEL) {
    return '';
  }

    // If the node is a child (H7 level), return the account count if available, otherwise return an empty string.
    if (node.level === this.CHILD_LEVEL) {
      return node.accountCount ? `${node?.children?.[0]?.hierarchyLevelName ?? 'Customer Number'} - ${node.accountCount}` : '';
    }

    return [
      node.childCount && `${node?.children?.[0]?.hierarchyLevelName ?? 'H7'} - ${node.childCount}`,
      node.accountCount && `${node?.children?.[0]?.children?.[0]?.hierarchyLevelName ?? 'Customer Number'} - ${node.accountCount}`
    ].filter(Boolean).join(' | ');
  }

  trackById(_index: number, item: { id?: string }): string {
    return item.id ?? '';
  }

  private async loadHierarchy(): Promise<void> {
    this.isLoading = true;
    this.hasError = false;
    this.nodes = [];
    this.expanded.clear();

    try {
      if (!this.filterSet) {
        throw new Error('filterSet is undefined');
      }

      const response = await this.filterRequests.getHierarchy(
        this.filtersService.selectedDivision,
        this.filterSet
      );

      this.criteria = response?.criteria;
      this.nodes = response?.nodes ?? [];
    } catch (error) {
      console.error('Error loading view selection hierarchy', error);
      this.hasError = true;
    } finally {
      this.isLoading = false;
      // The axios promise resolves outside Angular's zone, so the view needs a nudge.
      this.cdr.detectChanges();
    }
  }
}
